import { useEffect, useState } from 'react';
import { Card, Row, Col, Form } from 'react-bootstrap';
import PageBreadcrumb from '../../components/common/PageBreadcrumb';
import { defaultBreadcrumbItems } from '../../data/commonData';
import api from 'app/api/axios-api';
import { useGetDivisionsQuery } from '../divisions/divisions.api';
import Avatar from '@mui/material/Avatar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import IconButton from '@mui/material/IconButton';

interface User {
  _id: string;
  username: string;
  name: string | null;
  first_name: string;
  last_name: string;
  organization_id: string;
  organization_name: string;
  division_id?: string;
  role?: string;
  feature_id: number;
  environment: string;
  created_at: {
    $date: string;
  };
  client_credentials?: {
    dev?: { client_id: string };
    prod?: { client_id: string };
  };
}

interface UsersResponse {
  message: string;
  organizationId: string;
  totalUsers: number;
  users: User[];
}

const ViewAll = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedDivision, setSelectedDivision] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('');

  // Get divisions for filter dropdown
  const organizationId = localStorage.getItem('organizationId') || '';
  const { data: divisionsData } = useGetDivisionsQuery(organizationId, {
    skip: !organizationId
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const organizationId = localStorage.getItem('organizationId');

        if (!organizationId) {
          setError('Organization ID not found. Please log in again.');
          setIsLoading(false);
          return;
        }

        const response = await api.get<UsersResponse>(`auth/organization/${organizationId}/users`);

        setUsers(response.data.users);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to fetch users');
        console.error('Error fetching users:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Apply filters when users, selectedDivision, or selectedRole changes
  useEffect(() => {
    let filtered = users;

    if (selectedDivision) {
      // Filter by division_id
      filtered = filtered.filter(user => user.division_id === selectedDivision);
    }

    if (selectedRole) {
      filtered = filtered.filter(user => user.role === selectedRole);
    }

    setFilteredUsers(filtered);
  }, [users, selectedDivision, selectedRole, divisionsData]);

  const formatDate = (dateObj: { $date: string }) => {
    return new Date(dateObj.$date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}?`)) {
      return;
    }

    try {
      const response = await api.delete(`auth/user/${userId}`);

      console.log('Delete response:', response.data);

      // Remove deleted user from the list
      setUsers(users.filter(user => user._id !== userId));

      alert('User deleted successfully');
    } catch (err: any) {
      console.error('Error deleting user:', err);
      alert(err?.response?.data?.message || 'Failed to delete user');
    }
  };

  if (isLoading) {
    return (
      <div className="px-4 px-lg-6 pt-6 pb-9">
        {/* <PageBreadcrumb items={defaultBreadcrumbItems} /> */}
        <div className="text-center py-5">
          <p className="text-muted">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 px-lg-6 pt-6 pb-9">
        <PageBreadcrumb items={defaultBreadcrumbItems} />
        <div className="text-center py-5">
          <p className="text-danger">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 px-lg-6 pt-6 pb-9">
      {/* <PageBreadcrumb items={defaultBreadcrumbItems} /> */}

      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h2>All Users</h2>
            <p className="text-muted">Manage users in your organization</p>
          </div>
        </div>

        {/* Filter Dropdowns */}
        <Row className="g-3 mb-4">
          <Col xs={12} sm={6} md={4}>
            <Form.Group>
              <Form.Label>Filter by Division</Form.Label>
              <Form.Select value={selectedDivision} onChange={e => setSelectedDivision(e.target.value)}>
                <option value="">All Divisions</option>
                {divisionsData?.divisions?.map(division => (
                  <option key={division._id} value={division._id}>
                    {division.title}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col xs={12} sm={6} md={4}>
            <Form.Group>
              <Form.Label>Filter by Role</Form.Label>
              <Form.Select value={selectedRole} onChange={e => setSelectedRole(e.target.value)}>
                <option value="">All Roles</option>
                <option value="Admin">Admin</option>
                <option value="Developer">Developer</option>
                <option value="Reviewer">Reviewer</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <div className="mb-3">
          <small className="text-muted">
            Showing {filteredUsers.length} of {users.length} users
          </small>
        </div>
      </div>

      <Row className="g-3">
        {filteredUsers.map(user => (
          <Col key={user._id} xs={12} sm={6} lg={4} xl={3}>
            <Card className="h-100">
              <Card.Body>
                <div className="d-flex align-items-center mb-3 gap-3">
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: 'primary.main',
                      fontSize: '1.2rem',
                      fontWeight: 600
                    }}
                  >
                    {user.first_name?.[0] || ''}
                    {user.last_name?.[0] || ''}
                  </Avatar>
                  <div className="flex-grow-1">
                    <h5 className="mb-1">
                      {user.first_name} {user.last_name}
                    </h5>
                    <p className="text-muted small mb-0">{user.username}</p>
                  </div>
                </div>

                <div className="mb-3">
                  {user.organization_name && (
                    <small className="text-muted d-block mb-1">
                      <strong>Organization:</strong> {user.organization_name}
                    </small>
                  )}
                  <small className="text-muted d-block mb-1">
                    <strong>Environment:</strong> {user.environment}
                  </small>
                  {user.role && (
                    <small className="text-muted d-block mb-1">
                      <strong>Role:</strong> {user.role}
                    </small>
                  )}
                  <small className="text-muted d-block">
                    <strong>Created:</strong> {formatDate(user.created_at)}
                  </small>
                </div>

                {/* Delete Button */}
                <div className="d-flex justify-content-end mt-3">
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteUser(user._id, `${user.first_name} ${user.last_name}`);
                    }}
                    sx={{
                      color: 'black',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                        color: 'black'
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </IconButton>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {filteredUsers.length === 0 && users.length > 0 && (
        <div className="text-center py-5">
          <p className="text-muted">No users match the selected filters</p>
        </div>
      )}

      {users.length === 0 && (
        <div className="text-center py-5">
          <p className="text-muted">No users found</p>
        </div>
      )}
    </div>
  );
};

export default ViewAll;
